import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, of, Subject, throwError, BehaviorSubject } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';

import { ARTWORK_STATUSES } from '../../../requests/constants/artwork-file-statuses.constants';
import { File } from '../../../requests/models/file.model';
import { RequestsService } from '../../../requests/services/requests.service';
import { FILE_TYPES } from '../../constants/upload-restrictions.constants';
import { RestrictionOptionsObject } from '../../models/upload-file-restrictions.model';
import { Events, EventService } from '../../services/event-service.service';
import { LoaderService } from '../../services/loader.service';
import { MessageService } from '../../services/message-service.service';
import { UtilsService } from '../../services/utils.service';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public uploadFunction: (file: any) => Observable<any>;
  @Input() public beforeUploadFunction?: () => Observable<any>;
  @Input() public removeFunction: (idWrapper: any, fileItem?: File) => Observable<any>;
  @Input() public uploaderLabel: string;
  @Input() public moreLabel: string;
  @Input() public isDisabled: boolean;
  @Input() public initialFiles: any = [];
  @Input() public restrictionOptions?: RestrictionOptionsObject;
  // tslint:disable-next-line:no-unused-variable
  @Input() private type: string;
  @Input() public requestId: string | number;
  @Input() public itemToLinkId: string | number;
  @Input() public testId?: string;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() public onUpdateFiles: EventEmitter<any> = new EventEmitter<any>();
  @Input() public usePageMaskLoader = true;
  @Input() public isRequired = false;
  @Input() public isPreviewEnabled = false;
  @Input() showDownloadAll = false;

  @ViewChild('fileDropRef', {static: false}) fileDropEl: ElementRef;

  public files: any = [];
  public ARTWORK_STATUSES = ARTWORK_STATUSES;
  public isRestrictionSizeWarningExist: boolean;
  public fileTypesToAccept: string[] = [];

  private filesToUploadNumber = 0;
  private proceededFilesNumber = 0;
  protected ngUnsubscribe = new Subject();
  private filesLoadingSubject = new BehaviorSubject([]);
  numberOfFilesLoading$ = this.filesLoadingSubject.pipe(map(files => files.length));

  constructor(protected requestService: RequestsService,
              public utilsService: UtilsService,
              protected cd: ChangeDetectorRef,
              private messageService: MessageService,
              private translate: TranslatePipe,
              protected loaderService: LoaderService,
              protected eventService: EventService) {
  }

  public ngOnInit() {
    this.requestService.getFileTypes()
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(data => {
        if (data) {
          this.fileTypesToAccept = this.restrictionOptions?.fileType ?
            data[this.restrictionOptions?.fileType] :
            data[FILE_TYPES.DEFAULT];
          this.cd.markForCheck();
        }
      });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.initialFiles?.currentValue) {
      this.files = this.initialFiles.map(proof => ({ ...proof, progress: 100 }));
    }
  }

  private fileTypeAccepted(file: any) {
    if (!this.fileTypesToAccept || this.fileTypesToAccept.length === 0) {
      return true;
    }
    const fileTypeAccepted = this.fileTypesToAccept.indexOf(file.type) > -1;
    const [,fileExtension] = /(?:\.([^.]+))?$/.exec(file.name);
    const fileExtensionAccepted = this.fileTypesToAccept.indexOf(`.${fileExtension.toLowerCase()}`) > -1;

    return fileTypeAccepted || fileExtensionAccepted;
  }

  private filterFileTypes(files: Array<any>): Array<any> {
    const filtered = files.filter(this.fileTypeAccepted.bind(this));

    if (filtered.length !==  files.length) {
      this.messageService.showSystemMessage('error',
        this.translate.transform('common.errors.file-not-supported'));
    }

    return filtered;
  }

  /**
   * on file drop handler
   */
  onFileDropped($event: FileList) {
    const filtered = this.filterFileTypes(Array.from($event));

    if (!this.isDisabled) {
      this.isRestrictionSizeWarningExist = false;
      this.prepareFilesList(filtered);
    }
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(files: FileList) {
    const filtered = this.filterFileTypes(Array.from(files));
    this.isRestrictionSizeWarningExist = false;
    this.prepareFilesList(Array.from(filtered));
  }

  /**
   * Download file from server
   *
   * @param index (File index)
   */
  public downloadFile(index: number) {
    if (this.files[index].url) {
      const fileUrl = this.files[index].url;
      this.requestService.downloadFile(fileUrl, this.files[index].fileName);
    } else {
      const attachmentId = this.files[index].attachmentId || this.files[index].id;
      this.requestService.downloadAttachment(this.requestId, attachmentId);
    }
  }

  /**
   * Download and open file for preview in a separate browser tab
   *
   * @param index (File index)
   */
  public openFileForPreview(index: number): void {
    const file = this.files[index];
    const fileUrl = file?.url || this.requestService.getFileUrl(file, this.requestId);
    this.requestService.getBlob(fileUrl).subscribe(data => {
      const blob = new Blob([data.body], {type: data.headers.get('content-type') || 'application/pdf'});
      const link = URL.createObjectURL(blob);
      window.open(link, '_blank');
    });
  }

  /**
   * Delete file from files list. Using for currently uploaded files
   *
   * @param index (File index)
   */
  deleteFile(index: number) {
    if (this.files[index].progress < 100) {
      return; // Upload in progress.
    }

    if (this.files[index].uploadFailed) {
      this.files.splice(index, 1);
      return;
    }
    if (this.usePageMaskLoader) {
      this.loaderService.showLoader();
    }
    const attachmentId = this.files[index].attachmentId || this.files[index].id;
    this.removeFunction(attachmentId, this.files[index])
    .pipe(
      this.triggerActionHistoryUpdate()
    )
    .subscribe(() => {
      this.files.splice(index, 1);
      if (this.usePageMaskLoader) {
        this.loaderService.hideLoader();
      }

      this.onUpdateFiles.emit({action: 'delete', data: attachmentId});
      this.cd.markForCheck();
    });
  }

  /**
   * Convert Files list to normal array list
   *
   * @param files (Files List)
   */
  prepareFilesList(files: Array<any>) {
    const beforeUploadFunction = this.beforeUploadFunction || (() => of(true));

    if (!files.length) {
      return;
    }

    if (this.usePageMaskLoader) {
      this.loaderService.showLoader();
    }
    this.filesToUploadNumber = files.length;

    beforeUploadFunction().subscribe(() => {
      this.filesLoadingSubject.next([...this.filesLoadingSubject.value, ...files]);
      for (const item of files) {
        if (this.isValidByRestrictionsOptions(item)) {
          item.progress = 0;
          this.files = [...this.files, item];
          if (!item.uploaded) {
            const uploadObservable = this.uploadFunction(item);

            uploadObservable.pipe(map((event) => {
                this.cd.markForCheck();
                switch (event.type) {
                  case HttpEventType.UploadProgress:
                    const progress = Math.round(100 * event.loaded / event.total);
                    return {progress, data: null};
                  case HttpEventType.Response:
                    return {progress: 100, data: event.body};
                  default:
                    return `Unhandled event: ${event.type}`;
                }
              }),
              finalize(() => {
                this.filesLoadingSubject.next(this.filesLoadingSubject.value.filter(f => f !== item));
              }),
              this.triggerActionHistoryUpdate()
            )
            .subscribe((progressObject: string | { progress: number; data: any }) => {
                if (!progressObject || typeof progressObject === 'string') {
                  return;
                }
                item.progress = progressObject.progress;
                item.uploaded = item.progress === 100;

                if (typeof progressObject === 'object' && progressObject.data) {
                  item.attachmentId = progressObject.data.id || progressObject.data.attachmentId;
                  item.url = progressObject.data.url;

                  if (this.itemToLinkId) {
                    this.requestService.linkFileToReqItem(this.requestId, this.itemToLinkId, progressObject.data.id)
                      .subscribe(() => {
                        this.proceededFilesNumber ++;
                        this.hideLoader();
                        this.messageService.showSystemMessage('success',
                          item.name + ' ' + this.translate.transform('module.requests.upload.success-upload'));
                      }, error => throwError(error));
                  } else {
                    this.proceededFilesNumber ++;
                    this.hideLoader();
                    this.messageService.showSystemMessage('success',
                      item.name + ' ' + this.translate.transform('module.requests.upload.success-upload'));
                  }

                  this.onUpdateFiles.emit({action: 'add', data: progressObject.data});
                  this.cd.markForCheck();
                }
              }, (error: HttpErrorResponse) => {
                this.proceededFilesNumber ++;
                this.hideLoader();
                item.uploadFailed = true;
                this.cd.markForCheck();
              });
          }
        } else {
          this.proceededFilesNumber ++;
          this.hideLoader();
        }
      }
    });

    this.fileDropEl.nativeElement.value = '';
  }

  private hideLoader(): void {
    if (this.proceededFilesNumber === this.filesToUploadNumber) {
      if (this.usePageMaskLoader) {
        this.loaderService.hideLoader();
      }
      this.proceededFilesNumber = 0;
      this.filesToUploadNumber = 0;
    }
  }

  protected triggerActionHistoryUpdate() {
    return finalize(() => {
      this.eventService.emit({eventName: Events.UpdateActionHistory});
    });
  }

  private isValidByRestrictionsOptions(file): boolean {
    let fileValidBySize = true;

    if (this.restrictionOptions?.fileSize) {
      fileValidBySize = UtilsService.isFileFitsBySize(file.size, this.restrictionOptions.fileSize);
      this.isRestrictionSizeWarningExist = this.isRestrictionSizeWarningExist ? true : !fileValidBySize;
    }

    return fileValidBySize;
  }

  public downloadAll(): void {
    this.files.forEach(item => this.downloadItem(item));
  }

  public downloadItem(item): void {
    this.requestService.downloadItemFile(item, this.requestId);
  }
}
